import React, { useState, useEffect, useRef, useCallback } from 'react'
import { getSocket } from '../../socket/client.js'
import { useNavigate } from 'react-router'
import { Bell, CheckCheck, ExternalLink } from 'lucide-react'
import { clientApi } from '../../api/client-portal.js'
import { useClientAuthStore } from '../../store/clientAuthStore.js'

/**
 * Fallback poll cadence. Live updates arrive over the socket
 * (client:notification:new); the poll only covers a dropped connection or a
 * missed event, so it can be slow. Hidden tabs do not poll at all.
 */
const POLL_INTERVAL_MS = 5 * 60_000

export interface ClientNotificationItem {
  id: string
  title: string
  body: string
  actionUrl: string | null
  eventType: string
  isRead: boolean
  readAt: string | null
  createdAt: string
}

function formatRelativeTime(dateStr: string): string {
  try {
    const diffMs = Date.now() - new Date(dateStr).getTime()
    const diffSec = Math.floor(diffMs / 1000)
    if (diffSec < 60) return 'Just now'
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHour = Math.floor(diffMin / 60)
    if (diffHour < 24) return `${diffHour}h ago`
    const diffDay = Math.floor(diffHour / 24)
    if (diffDay < 7) return `${diffDay}d ago`
    return new Date(dateStr).toLocaleDateString()
  } catch {
    return dateStr
  }
}

export function ClientNotificationBellWidget(): React.ReactElement | null {
  const { accessToken } = useClientAuthStore()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<ClientNotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showPushBanner, setShowPushBanner] = useState(false)
  const [pushLoading, setPushLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator) {
      if (Notification.permission === 'default') {
        setShowPushBanner(true)
      }
    }
  }, [])

  const handleEnablePush = async () => {
    try {
      setPushLoading(true)
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') {
        setShowPushBanner(false)
        return
      }

      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
      await navigator.serviceWorker.ready

      const res = await fetch('/api/notifications/push/vapid-public-key')
      const { publicKey } = (await res.json()) as { publicKey?: string }
      if (!publicKey) {
        setShowPushBanner(false)
        return
      }

      const padding = '='.repeat((4 - (publicKey.length % 4)) % 4)
      const base64 = (publicKey + padding).replace(/-/g, '+').replace(/_/g, '/')
      const rawData = window.atob(base64)
      const buffer = new ArrayBuffer(rawData.length)
      const outputArray = new Uint8Array(buffer)
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: buffer,
      })

      const subJson = subscription.toJSON()
      if (subJson.endpoint && subJson.keys?.p256dh && subJson.keys?.auth) {
        await clientApi.post('/client-portal/notifications/push/subscribe', {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
          userAgent: navigator.userAgent,
        })
      }
      setShowPushBanner(false)
    } catch {
      setShowPushBanner(false)
    } finally {
      setPushLoading(false)
    }
  }

  const fetchNotifications = useCallback(async () => {
    if (!accessToken) return
    try {
      const res = await clientApi.get<{
        notifications: ClientNotificationItem[]
        unreadCount: number
      }>('/client-portal/notifications?limit=15')
      setNotifications(res.notifications ?? [])
      setUnreadCount(res.unreadCount ?? 0)
    } catch {
      // Non-critical background failure
    }
  }, [accessToken])

  useEffect(() => {
    if (!accessToken) return
    // Poll only while the tab is visible, and refresh the moment it becomes
    // visible again. Before, every open portal tab hit the API every 25 s even
    // when hidden or left open overnight.
    let timer: ReturnType<typeof setInterval> | null = null
    const start = () => {
      if (timer) return
      timer = setInterval(fetchNotifications, POLL_INTERVAL_MS)
    }
    const stop = () => {
      if (timer) clearInterval(timer)
      timer = null
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications()
        start()
      } else {
        stop()
      }
    }
    // Live push: the gateway relays RabbitMQ's client.notification.created to
    // this client's room. Refresh when one arrives, and after a reconnect (events
    // sent while disconnected are not replayed).
    const socket = getSocket()
    const onPush = () => fetchNotifications()
    socket.on('client:notification:new', onPush)
    socket.on('connect', onPush)

    fetchNotifications()
    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
      socket.off('client:notification:new', onPush)
      socket.off('connect', onPush)
    }
  }, [accessToken, fetchNotifications])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  if (!accessToken) return null

  const handleMarkAllAsRead = async () => {
    try {
      await clientApi.post('/client-portal/notifications/read-all')
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
      setUnreadCount(0)
    } catch {
      // Non-critical
    }
  }

  const handleNotificationClick = async (notif: ClientNotificationItem) => {
    if (!notif.isRead) {
      try {
        await clientApi.patch(`/client-portal/notifications/${notif.id}/read`)
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n)),
        )
        setUnreadCount((c) => Math.max(0, c - 1))
      } catch {
        // Non-critical
      }
    }

    setIsOpen(false)
    if (notif.actionUrl) {
      navigate(notif.actionUrl)
    }
  }

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          setIsOpen((prev) => !prev)
          if (!isOpen) fetchNotifications()
        }}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none transition-colors rounded-full hover:bg-gray-100"
        aria-label="View notifications"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-600 rounded-full border-2 border-white shadow-sm animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in duration-150">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50/80 border-b border-gray-100">
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-gray-800 text-sm">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[11px] font-medium bg-blue-100 text-blue-700 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                title="Mark all as read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span>Mark all read</span>
              </button>
            )}
          </div>

          {showPushBanner && (
            <div className="px-4 py-2.5 bg-blue-50/70 border-b border-blue-100 flex items-center justify-between text-xs">
              <span className="text-blue-900 font-medium">Get desktop alerts for replies</span>
              <button
                type="button"
                disabled={pushLoading}
                onClick={handleEnablePush}
                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-[11px] transition-colors shadow-sm"
              >
                {pushLoading ? 'Enabling...' : 'Enable'}
              </button>
            </div>
          )}

          {/* List */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300 opacity-60" />
                <p className="text-xs">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => void handleNotificationClick(notif)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      void handleNotificationClick(notif)
                    }
                  }}
                  className={`px-4 py-3 cursor-pointer transition-colors hover:bg-blue-50/40 flex items-start space-x-3 ${
                    !notif.isRead ? 'bg-blue-50/20' : 'bg-white'
                  }`}
                >
                  <div className="mt-1 flex-shrink-0">
                    {!notif.isRead ? (
                      <span className="block w-2 h-2 rounded-full bg-blue-600 ring-2 ring-blue-200" />
                    ) : (
                      <span className="block w-2 h-2 rounded-full bg-transparent" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-xs font-semibold truncate ${!notif.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                        {notif.title}
                      </p>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2">
                        {formatRelativeTime(notif.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                      {notif.body}
                    </p>
                    {notif.actionUrl && (
                      <div className="flex items-center space-x-1 text-[11px] text-blue-600 mt-1.5 font-medium hover:underline">
                        <span>View Conversation</span>
                        <ExternalLink className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
